import { context, trace } from '@opentelemetry/api'
import { useEffect, useMemo, useRef } from 'react'
import { matchRoutes, useLocation } from 'react-router-dom'
import { mainRoutes } from '../routes'
import { setActiveScreenSpan } from './activeScreenSpan'

const tracer = trace.getTracer('entity-console')

const normalizeRoutePath = (routePath: string) => routePath.replace(/:([A-Za-z0-9_]+)/g, '{$1}')

const getRouteName = (pathname: string) => {
  const matches = matchRoutes(mainRoutes, pathname)
  const lastMatch = matches ? matches[matches.length - 1] : undefined
  const routePath = lastMatch?.route?.path
  if (routePath && routePath.length > 0) {
    return normalizeRoutePath(routePath)
  }
  return pathname || '/'
}

type ScreenSpanTrackerProps = {
  organizationId?: string
  userEntityId?: string
}

const ScreenSpanTracker = ({ organizationId, userEntityId }: ScreenSpanTrackerProps) => {
  const location = useLocation()
  const spanRef = useRef<ReturnType<typeof tracer.startSpan> | null>(null)
  const originalFetchRef = useRef<typeof window.fetch | null>(null)
  const originalXhrSendRef = useRef<XMLHttpRequest['send'] | null>(null)

  const routeName = useMemo(() => `Screen: ${getRouteName(location.pathname)}`, [location.pathname])

  useEffect(() => {
    if (spanRef.current) {
      spanRef.current.end()
    }
    const span = tracer.startSpan(routeName, {
      attributes: {
        'screen.name': routeName,
        ...(organizationId ? { 'organization.id': organizationId } : {}),
        ...(userEntityId ? { 'user.id': userEntityId } : {}),
      },
    })
    spanRef.current = span
    // Publish so the asset-preview runtime tracing can parent its session under this screen span.
    setActiveScreenSpan(span)

    if (!originalFetchRef.current) {
      originalFetchRef.current = window.fetch.bind(window)
    }
    if (!originalXhrSendRef.current) {
      originalXhrSendRef.current = XMLHttpRequest.prototype.send
    }

    const spanContext = trace.setSpan(context.active(), span)
    if (originalFetchRef.current) {
      window.fetch = context.bind(spanContext, originalFetchRef.current)
    }
    if (originalXhrSendRef.current) {
      XMLHttpRequest.prototype.send = context.bind(spanContext, originalXhrSendRef.current)
    }

    return () => {
      if (spanRef.current) {
        spanRef.current.end()
        spanRef.current = null
      }
      setActiveScreenSpan(undefined)
      if (originalFetchRef.current) {
        window.fetch = originalFetchRef.current
      }
      if (originalXhrSendRef.current) {
        XMLHttpRequest.prototype.send = originalXhrSendRef.current
      }
    }
  }, [location.pathname, routeName])

  useEffect(() => {
    if (!spanRef.current) return
    if (organizationId) {
      spanRef.current.setAttribute('organization.id', organizationId)
    }
    if (userEntityId) {
      spanRef.current.setAttribute('user.id', userEntityId)
    }
  }, [organizationId, userEntityId])

  return null
}

export default ScreenSpanTracker
